import HLS from 'hls.js';
import { ConfirmLiveSwitch } from 'uicomponents';

import { LiveVideoRoomState } from '.';
import { LiveVideoRoomGetters } from './getters';
import { LiveVideoRoomMutations } from './mutations';
import {
  isCurrentRouteABroadcast,
  getCurrentBroadcast,
  isBroadcastRunning,
  isLiteModeActiveAndBeingUsed,
  shouldStartVideoPlayback,
  clearTimeoutsAndIntervals,
  resetVideoRoomState,
  initializeHLSJSVideoPlayback,
  initializeVideoPlaybackInIOS,
  doPostVideoPlaybackCleanup,
  clearVideoPlaybackStateTimeouts,
  initializeRtmpHlsTracking
} from './utils';

import { HLS_PLAYBACK } from '@/constants';
import DialogService from '@/services/dialogs/DialogService';
import { playVideoInLiveBroadcast, stopPlayingVideoInLiveBroadcast } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { ActivityType, handleSpeakerActivity } from '@/services/speakerActivity.service';
import { RootState } from '@/store';

export interface LiveVideoRoomContext {
  state: LiveVideoRoomState;
  rootState: RootState;
  getters: LiveVideoRoomGetters;
  rootGetters: { [k: string]: any };
  commit: <k extends keyof LiveVideoRoomMutations>(
    mutation: k | 'event/stage/toggleMuted',
    payload?: any,
    config?: { root: boolean }
  ) => any;
  dispatch: (action: string, params?: any, settings?: { root: boolean }) => Promise<any>;
}

const actions = {
  handleAgendaVideoPlaybackState(context: LiveVideoRoomContext) {
    if (!isCurrentRouteABroadcast()) {
      context.dispatch('cleanupLiveVideoStore');
      return;
    }
    const broadcastData = getCurrentBroadcast(context);

    const hasAdminAccess =
      context.rootGetters['event/isBroadcastSpeakerOrOrganizer'] && !context.rootState.event.stage?.isTVMode;

    const shouldPlayVIdeo = hasAdminAccess || isBroadcastRunning(context, broadcastData);

    const isLiteModeActive: boolean = isLiteModeActiveAndBeingUsed(context, broadcastData);

    if (
      !broadcastData?.current_playing_video_asset ||
      !broadcastData?.playback_start_time ||
      isLiteModeActive ||
      !shouldPlayVIdeo
    ) {
      context.dispatch('cleanupLiveVideoStore');
    } else {
      context.dispatch('playVideoInBroadcast');
    }
  },
  playVideoInBroadcast(context: LiveVideoRoomContext) {
    if (!shouldStartVideoPlayback(context)) {
      return;
    }

    clearTimeoutsAndIntervals(context);
    resetVideoRoomState(context);
    context.state.hasVideoBufferPlaybackStalled = true;
    context.dispatch('simuliveroom/destroySimuliveBroadcastPlaybackContext', null, { root: true });
    if (HLS.isSupported()) {
      initializeHLSJSVideoPlayback(context);
    } else if (context.state.hlsVideoElm.canPlayType('application/vnd.apple.mpegurl')) {
      initializeVideoPlaybackInIOS(context);
    } else {
      context.state.isHLSStreamingSupported = false;
    }
  },
  cleanupLiveVideoStore(context: LiveVideoRoomContext) {
    if (context.state.hlsContext) {
      context.state.hlsContext.destroy();
    }
    clearTimeoutsAndIntervals(context);
    resetVideoRoomState(context);
    return doPostVideoPlaybackCleanup(context);
  },
  updateHLSPlayerMuteState(context: LiveVideoRoomContext) {
    context.state.hlsVideoElm.muted = context.rootState.event.stage?.muted;
  },
  switchToVideoMode(context: LiveVideoRoomContext, videoId: string) {
    if (context.state.liveVideoRoomVideoPlaybackStartState !== 'IDLE' && context.state.idOfSelectedVideo === videoId) {
      return;
    }

    const broadcast = getCurrentBroadcast(context);
    if (broadcast.broadcast_type !== 'LIVE_HYBRID' || broadcast.current_playing_video_asset === videoId) {
      return;
    }
    clearVideoPlaybackStateTimeouts(context);
    context.state.idOfSelectedVideo = videoId;
    context.state.liveVideoRoomVideoPlaybackStartState = 'LOADING';

    playVideoInLiveBroadcast(broadcast.id, videoId)
      .then(() => {
        handleSpeakerActivity(() => Promise.resolve(), ActivityType.VideoShareStart, ActivityType.VideoShareFailed);
        context.state.liveVideoRoomVideoPlaybackStartState = 'SUCCESS';
        context.state.videoPlaybackStartStateTimeout = setTimeout(() => {
          context.state.idOfSelectedVideo = '';
          context.state.liveVideoRoomVideoPlaybackStartState = 'IDLE';
        }, HLS_PLAYBACK.WAIT_TIME_BEFORE_ALLOWING_NEXT_VIDEO_PLAYBACK_SELECTION);
      })
      .catch(err => {
        handleSpeakerActivity(() => Promise.reject(err), ActivityType.VideoShareFailed, ActivityType.VideoShareFailed);
        loggerService.error('Error switching to video mode!', err);
        context.state.liveVideoRoomVideoPlaybackStartState = 'ERROR';
        context.state.videoPlaybackStartStateTimeout = setTimeout(() => {
          context.state.idOfSelectedVideo = '';
          context.state.liveVideoRoomVideoPlaybackStartState = 'IDLE';
        }, 10000);
      });
  },
  switchToSpeakerMode(context: LiveVideoRoomContext) {
    const broadcast = getCurrentBroadcast(context);
    if (broadcast.broadcast_type !== 'LIVE_HYBRID') {
      return;
    }
    const res = DialogService.show(ConfirmLiveSwitch, {}, {
      cancelOnClickOutside: false
    } as any);
    res.promise
      .then(() => {
        context.state.isSwitchingToSpeakerMode = true;
        context.state.hasErrorWhenSwitchingToSpeakerMode = false;
        return stopPlayingVideoInLiveBroadcast(broadcast.id);
      })
      .then(() => {
        handleSpeakerActivity(() => Promise.resolve(), ActivityType.VideoShareStop, ActivityType.VideoShareStopFailed);
        context.state.isSwitchingToSpeakerMode = false;
        context.state.hasErrorWhenSwitchingToSpeakerMode = false;
      })
      .catch(() => {
        handleSpeakerActivity(
          () => Promise.reject(),
          ActivityType.VideoShareStopFailed,
          ActivityType.VideoShareStopFailed
        );
        context.state.hasErrorWhenSwitchingToSpeakerMode = true;
        setTimeout(() => {
          context.state.isSwitchingToSpeakerMode = false;
          context.state.hasErrorWhenSwitchingToSpeakerMode = false;
        }, 10000);
      })
      .finally(() => {
        clearVideoPlaybackStateTimeouts(context);
        context.state.idOfSelectedVideo = '';
        context.state.liveVideoRoomVideoPlaybackStartState = 'IDLE';
      });
  },
  setRtmpHlsContext(context: LiveVideoRoomContext, hlsInstance: any) {
    initializeRtmpHlsTracking(context, hlsInstance);
  },
  setActiveSpeakerDevice(context: LiveVideoRoomContext) {
    const speakerDeviceID = context.rootState.mediaroom.currentDevices?.speaker?.deviceId;

    if (!speakerDeviceID) {
      return;
    }
    try {
      context.state.hlsVideoElm.setSinkId(speakerDeviceID).catch(function (error: Error) {
        let errorMessage = error.message;
        if (error.name === 'SecurityError') {
          errorMessage = 'You need to use HTTPS for selecting audio output ' + 'device: ' + error.message;
        }
        throw errorMessage;
      });
    } catch (error) {
      loggerService.error('error setting active speaker:', error);
    }
  },
  showHlsVideoPlaybackError(context: LiveVideoRoomContext, promptLiteMode: Boolean = true) {
    const i18n = context.rootState.event.translator?.i18n ?? { global: { t: (k: string) => k } };
    const bodyText = promptLiteMode
      ? i18n.global.t(
          '@@translate@@ We detected an error while playing the video! If you are facing issues with the video playback, please try refreshing the page or switching to lite mode.'
        )
      : i18n.global.t(
          '@@translate@@ We detected an error while playing the video! If you are facing issues with the video playback, please try refreshing the page.'
        );
    context.dispatch(
      'mediaroom/handleNetworkError',
      {
        type: 'HLS_PLAYBACK_ERROR',
        title: i18n.global.t('@@translate@@ Error while playing video.'),
        bodyText: bodyText
      },
      { root: true }
    );
  }
};

export default actions;
